/*
 * GLAS-FINDER (Quiz) – sections/glas-finder.liquid
 *
 * - Ein Screen pro Frage, kein Neuladen. Browser-Zurück springt zwischen den Screens (History-State ohne URL-Änderung).
 * - Ergebnis auf derselben Seite: <glas-finder-ergebnis> (sections/glas-finder-ergebnis.liquid) zeigt es, die URL
 *   bekommt ?glas=<handles>. Fehlt die Ergebnis-Section, leitet das Quiz auf die Collection weiter.
 * - Fragen: Momente (immer, bis zu zwei) -> Womit starten (nur bei zwei Momenten) -> Einnahme (nur wenn ein gewählter
 *   Moment Kapsel UND Pulver hat). Also 1 bis 3 Fragen.
 * - "Womit starten" bestimmt "Dein Glas", der andere Moment wird "Passt auch zu dir" (Alternative, keine Kombi).
 * - Einnahme "Beides okay": Variante aus standardBeiBeides (Pulver). Bei nur einem Moment erscheint die andere Variante
 *   desselben Moments als "Passt auch zu dir".
 * - ?start=<produkt-handle> wählt den passenden Moment vor (Mail 3).
 * - Datenschutz: keine Speicherung (kein localStorage, keine Cookies). URL und Tracking enthalten nur Produkt-Handles.
 * - Tracking nur mit Einwilligung (Shopify Customer Privacy API, wird von Pandectes befüllt).
 * - Keine Kombi-Empfehlung in Version 1, siehe Kommentar in sections/glas-finder.liquid.
 */

const MAX_MOMENTE = 2;

const warten = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/* ------------------------------------------------------------------------------------------------------------------ */
/* Tracking                                                                                                            */
/* ------------------------------------------------------------------------------------------------------------------ */

const ladePrivacyApi = () =>
  new Promise((resolve) => {
    const shopify = window.Shopify;

    if (shopify && shopify.customerPrivacy) {
      resolve(shopify.customerPrivacy);
    } else if (shopify && typeof shopify.loadFeatures === 'function') {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (fehler) => {
        resolve(fehler ? null : window.Shopify.customerPrivacy || null);
      });
    } else {
      resolve(null);
    }
  });

const erlaubt = (privacy, methode) => {
  try {
    return Boolean(privacy && typeof privacy[methode] === 'function' && privacy[methode]());
  } catch (fehler) {
    return false;
  }
};

const sendeKlaviyo = (name, eigenschaften) => {
  const klaviyo = window.klaviyo;

  try {
    if (klaviyo && typeof klaviyo.track === 'function') {
      return Promise.resolve(klaviyo.track(name, eigenschaften));
    }

    if (klaviyo && typeof klaviyo.push === 'function') {
      klaviyo.push(['track', name, eigenschaften]);
    } else if (window._learnq && typeof window._learnq.push === 'function') {
      window._learnq.push(['track', name, eigenschaften]);
    }
  } catch (fehler) {
    // Tracking darf den Glas-Finder nie blockieren
  }

  return Promise.resolve();
};

/**
 * Sendet ein Customer Event (Analyse-Einwilligung) und optional ein Klaviyo-Event (Marketing-Einwilligung).
 * Ohne Einwilligung oder ohne Privacy-API wird nichts gesendet.
 */
const tracke = async (eventName, daten, klaviyoEvent) => {
  const privacy = await Promise.race([ladePrivacyApi(), warten(1000).then(() => null)]);

  if (!privacy) {
    return;
  }

  if (erlaubt(privacy, 'analyticsProcessingAllowed')) {
    try {
      window.Shopify.analytics.publish(eventName, daten || {});
    } catch (fehler) {
      // Shopify.analytics nicht verfügbar (z. B. lokale Vorschau)
    }
  }

  if (klaviyoEvent && erlaubt(privacy, 'marketingAllowed')) {
    await Promise.race([sendeKlaviyo(klaviyoEvent.name, klaviyoEvent.eigenschaften), warten(600)]);
  }
};

/* ------------------------------------------------------------------------------------------------------------------ */
/* Quiz                                                                                                                */
/* ------------------------------------------------------------------------------------------------------------------ */

export class GlasFinder extends HTMLElement {
  connectedCallback() {
    // Wieder eingehängt (z. B. nach DOM-Verschiebung): nur den Verlaufs-Listener erneuern
    if (this.bereit) {
      window.addEventListener('popstate', this.beiZurueckImBrowser);
      return;
    }

    // Im Theme-Editor bleiben alle Screens untereinander sichtbar (Klasse kommt serverseitig aus request.design_mode)
    if (this.classList.contains('glas-finder--editor') || (window.Shopify && window.Shopify.designMode)) {
      return;
    }

    try {
      this.daten = JSON.parse(this.querySelector('[data-glas-finder-daten]').textContent);
    } catch (fehler) {
      return;
    }

    this.bereit = true;
    this.auswahl = [];
    this.prioritaet = null;
    this.format = null;
    this.verlaufTiefe = 0;

    this.addEventListener('click', this.beiKlick.bind(this));
    this.addEventListener('change', this.beiAenderung.bind(this));

    this.beiZurueckImBrowser = this.beiZurueckImBrowser.bind(this);
    window.addEventListener('popstate', this.beiZurueckImBrowser);

    // Nach einem Neuladen mitten im Quiz beginnt es wieder am Start, alte Verlaufs-Zustände verwerfen
    if (window.history.state && window.history.state.glasFinder) {
      window.history.replaceState(null, '');
    }

    this.vorauswahlAusUrl();
    this.aktualisieren();
    this.zeigeScreen('start', { fokus: false });
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.beiZurueckImBrowser);
  }

  /* --- Daten ------------------------------------------------------------------------------------------------------ */

  moment(key) {
    return this.daten.momente.find((moment) => moment.key === key);
  }

  hatWahl(moment) {
    return Boolean(moment && moment.kapsel && moment.pulver);
  }

  brauchtEinnahme() {
    return this.auswahl.some((key) => this.hatWahl(this.moment(key)));
  }

  /** Die Fragen, die bei der aktuellen Auswahl nötig sind, in Reihenfolge */
  fragen() {
    const fragen = ['moment'];

    if (this.auswahl.length === 2) {
      fragen.push('prioritaet');
    }

    if (this.brauchtEinnahme()) {
      fragen.push('format');
    }

    return fragen;
  }

  /** Momente in Ergebnis-Reihenfolge: zuerst der aus "Womit starten", sonst die Klick-Reihenfolge */
  reihenfolge() {
    if (this.auswahl.length === 2 && this.auswahl.includes(this.prioritaet)) {
      return [this.prioritaet, ...this.auswahl.filter((key) => key !== this.prioritaet)];
    }

    return this.auswahl.slice();
  }

  ergebnis() {
    const reihenfolge = this.reihenfolge();
    const handles = [];
    let format = this.format;

    if (!format || format === 'beides') {
      format = this.daten.standardBeiBeides === 'kapsel' ? 'kapsel' : 'pulver';
    }

    reihenfolge.forEach((key) => {
      const moment = this.moment(key);
      let handle = null;

      if (!moment) {
        return;
      }

      if (this.hatWahl(moment)) {
        handle = format === 'pulver' ? moment.pulver : moment.kapsel;
      } else {
        handle = moment.kapsel || moment.pulver;
      }

      if (handle && !handles.includes(handle)) {
        handles.push(handle);
      }
    });

    // "Beides okay" mit nur einem Moment: die andere Variante als Alternative zeigen (nie als Kombi)
    const einzigerMoment = reihenfolge.length === 1 && this.moment(reihenfolge[0]);

    if (this.format === 'beides' && handles.length === 1 && this.hatWahl(einzigerMoment)) {
      handles.push(handles[0] === einzigerMoment.pulver ? einzigerMoment.kapsel : einzigerMoment.pulver);
    }

    return handles;
  }

  vorauswahlAusUrl() {
    const start = new URLSearchParams(window.location.search).get('start');

    if (!start) {
      return;
    }

    const handle = start.trim().toLowerCase();
    const moment = this.daten.momente.find((eintrag) => eintrag.kapsel === handle || eintrag.pulver === handle);
    const input = moment && this.momentInput(moment.key);

    if (input) {
      input.checked = true;
      this.auswahl = [moment.key];
    }
  }

  momentInput(key) {
    return Array.from(this.querySelectorAll('input[name="moment"]')).find((input) => input.value === key);
  }

  /* --- Ereignisse ------------------------------------------------------------------------------------------------- */

  beiKlick(event) {
    const button = event.target.closest('[data-aktion]');

    if (!button || button.disabled) {
      return;
    }

    switch (button.getAttribute('data-aktion')) {
      case 'start':
        tracke('glas_finder_gestartet', {});
        this.weiterZu('moment');
        break;

      case 'weiter': {
        const naechste = this.naechsteFrage(this.aktuellerScreen);

        if (naechste) {
          this.weiterZu(naechste);
        } else {
          this.abschliessen(button);
        }
        break;
      }

      case 'zurueck':
        this.zurueck();
        break;
    }
  }

  beiAenderung(event) {
    const input = event.target;

    if (input.name === 'moment') {
      if (input.checked) {
        if (this.auswahl.length >= MAX_MOMENTE) {
          input.checked = false;
          this.hinweisHervorheben();
          return;
        }

        this.auswahl.push(input.value);
      } else {
        this.auswahl = this.auswahl.filter((key) => key !== input.value);
      }
    } else if (input.name === 'prioritaet') {
      this.prioritaet = input.value;
    } else if (input.name === 'format') {
      this.format = input.value;
    }

    this.aktualisieren();
  }

  beiZurueckImBrowser(event) {
    const zustand = event.state || {};
    const ziel = zustand.glasFinder || 'start';
    const ergebnis = this.ergebnisSection();

    this.verlaufTiefe = zustand.tiefe || 0;

    // Vorwärts zurück ins Ergebnis
    if (ziel === 'ergebnis' && ergebnis) {
      ergebnis.zeige((new URLSearchParams(window.location.search).get('glas') || '').split(','), { scroll: false });
      return;
    }

    if (ergebnis && !ergebnis.hidden) {
      ergebnis.verstecke();
    }

    this.zeigeScreen(ziel === 'start' || this.fragen().includes(ziel) ? ziel : 'moment');
  }

  ergebnisSection() {
    const ergebnis = document.querySelector('glas-finder-ergebnis');
    return ergebnis && typeof ergebnis.zeige === 'function' ? ergebnis : null;
  }

  /* --- Navigation ------------------------------------------------------------------------------------------------- */

  naechsteFrage(screen) {
    const fragen = this.fragen();
    return fragen[fragen.indexOf(screen) + 1] || null;
  }

  weiterZu(screen) {
    try {
      window.history.pushState({ glasFinder: screen, tiefe: this.verlaufTiefe + 1 }, '');
      this.verlaufTiefe += 1;
    } catch (fehler) {
      // Ohne History-API funktioniert der Zurück-Button trotzdem
    }

    this.zeigeScreen(screen);
  }

  zurueck() {
    if (this.verlaufTiefe > 0) {
      window.history.back();
      return;
    }

    const ablauf = ['start', ...this.fragen()];
    this.zeigeScreen(ablauf[Math.max(0, ablauf.indexOf(this.aktuellerScreen) - 1)]);
  }

  zeigeScreen(name, { fokus = true } = {}) {
    this.aktuellerScreen = name;

    this.querySelectorAll('[data-screen]').forEach((screen) => {
      screen.hidden = screen.getAttribute('data-screen') !== name;
    });

    if (!fokus) {
      return;
    }

    const aktiv = this.querySelector(`[data-screen="${name}"]`);
    const ziel = aktiv && aktiv.querySelector('[data-fokus]');

    if (ziel) {
      ziel.focus({ preventScroll: true });
    }

    if (this.getBoundingClientRect().top < 0) {
      this.scrollIntoView({ block: 'start', behavior: this.bewegungReduziert() ? 'auto' : 'smooth' });
    }
  }

  bewegungReduziert() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* --- Anzeige ---------------------------------------------------------------------------------------------------- */

  aktualisieren() {
    // Priorität verfällt, wenn ihr Moment abgewählt wurde
    if (this.prioritaet && !this.auswahl.includes(this.prioritaet)) {
      this.prioritaet = null;
      this.querySelectorAll('input[name="prioritaet"]').forEach((input) => {
        input.checked = false;
      });
    }

    this.prioritaetOptionenAktualisieren();

    const fragen = this.fragen();
    // Vor der ersten Auswahl ist offen, wie viele Fragen kommen: mindestens zwei anzeigen
    const gesamt = this.auswahl.length === 0 ? 2 : fragen.length;
    const erfuellt = {
      moment: this.auswahl.length > 0,
      prioritaet: Boolean(this.prioritaet),
      format: Boolean(this.format),
    };

    fragen.forEach((name, index) => {
      const screen = this.querySelector(`[data-screen="${name}"]`);

      if (!screen) {
        return;
      }

      const fortschritt = screen.querySelector('[data-fortschritt]');
      const text = this.daten.fortschrittText.replace('[aktuell]', String(index + 1)).replace('[gesamt]', String(gesamt));

      if (fortschritt && fortschritt.textContent !== text) {
        fortschritt.textContent = text;
      }

      screen.querySelectorAll('[data-segment]').forEach((segment, segmentIndex) => {
        segment.hidden = segmentIndex >= gesamt;
        segment.classList.toggle('is-aktiv', segmentIndex <= index);
      });

      const button = screen.querySelector('[data-aktion="weiter"]');

      if (button) {
        const letzte = index === fragen.length - 1 && this.auswahl.length > 0;
        const beschriftung = button.getAttribute(letzte ? 'data-text-fertig' : 'data-text-weiter');

        button.disabled = !erfuellt[name];

        if (beschriftung && button.textContent.trim() !== beschriftung) {
          button.textContent = beschriftung;
        }
      }
    });

    this.classList.toggle('glas-finder--voll', this.auswahl.length >= MAX_MOMENTE);
  }

  /** Bei "Womit starten" nur die zwei gewählten Momente zeigen, in Klick-Reihenfolge */
  prioritaetOptionenAktualisieren() {
    const liste = this.querySelector('[data-prioritaet-optionen]');

    if (!liste) {
      return;
    }

    liste.querySelectorAll('[data-prioritaet-option]').forEach((option) => {
      option.hidden = !this.auswahl.includes(option.getAttribute('data-prioritaet-option'));
    });

    this.auswahl.forEach((key) => {
      const option = liste.querySelector(`[data-prioritaet-option="${key}"]`);

      if (option) {
        liste.appendChild(option);
      }
    });
  }

  hinweisHervorheben() {
    const hinweis = this.querySelector('[data-hinweis]');
    const ansage = this.querySelector('[data-ansage]');

    if (hinweis) {
      hinweis.classList.remove('is-hervorgehoben');
      void hinweis.offsetWidth; // Animation neu starten
      hinweis.classList.add('is-hervorgehoben');
    }

    if (ansage && hinweis) {
      ansage.textContent = '';
      setTimeout(() => {
        ansage.textContent = hinweis.textContent.trim();
      }, 60);
    }
  }

  /* --- Abschluss -------------------------------------------------------------------------------------------------- */

  async abschliessen(button) {
    const handles = this.ergebnis();
    const parameter = handles.length > 0 ? `?glas=${handles.map(encodeURIComponent).join(',')}` : '';
    const ergebnis = this.ergebnisSection();
    const event = {
      name: 'Glas-Finder abgeschlossen',
      eigenschaften: { empfehlung: handles },
    };

    // Ergebnis auf dieser Seite zeigen, die URL enthält nur die Produkt-Handles
    if (ergebnis && ergebnis.pruefe(handles).length > 0) {
      const adresse = new URL(window.location.href);

      adresse.search = parameter;
      adresse.hash = '';
      window.history.pushState({ glasFinder: 'ergebnis', tiefe: this.verlaufTiefe + 1 }, '', adresse.href);
      this.verlaufTiefe += 1;
      ergebnis.zeige(handles);
      tracke('glas_finder_abgeschlossen', { glas: handles }, event);
      return;
    }

    // Rückfall: auf die Collection weiterleiten (Tracking vorher abwarten)
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    if (handles.length > 0) {
      await tracke('glas_finder_abgeschlossen', { glas: handles }, event);
    }

    this.weiterleiten(this.daten.zielUrl + parameter);
  }

  weiterleiten(url) {
    window.location.assign(url);
  }
}

if (!window.customElements.get('glas-finder')) {
  window.customElements.define('glas-finder', GlasFinder);
}

/* Nach "Zurück" aus der Collection (Back-Forward-Cache) den Button wieder freigeben */
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) {
    return;
  }

  document.querySelectorAll('glas-finder [aria-busy="true"]').forEach((button) => {
    button.removeAttribute('aria-busy');
    button.disabled = false;
  });
});
